import { useEffect, useRef, useState } from 'react';
import useLang from 'Shared/hooks/useLanguage';
import { assignFormValues } from '../../../Utils/Auth';
import UseMedicationAutoComplete from '../../hooks/Requisition/UseMedicationAutoComplete';
import MuiSkeleton from '../MuiSkeleton';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

const ActiveMedicationListCheckbox = ({
  Inputs,
  index,
  options = [],
  sysytemFieldName,
  displayFieldName,
  depControlIndex,
  fieldIndex,
  isDependency,
  repeatFieldSection,
  isDependencyRepeatFields,
  repeatFieldIndex,
  repeatDependencySectionIndex,
  repeatDepFieldIndex,
  setInputs,
  infectiousData,
  setInfectiousData,
  ArrayReqId,
  labelClassName,
  spanClassName,
  loading,
  error,
  noActiveMedication,
  defaultValue,
  medicationSelections,
  setMedicationSelections,
  fieldConfiguration
}: any) => {
  const { t } = useLang();
  const location = useLocation();
  const inputSearchRef = useRef<HTMLInputElement>(null);
  const hasInitializedOtherMedicationsRef = useRef(false);
  const hasInitializedAssignedMedicationsRef = useRef(false);
  function FindIndex(arr: any[], text: any): number {
    return arr.findIndex(item => item.reqId === text);
  }

  const normalizeMedicationEntry = (opt: any) => {
    if (!opt) return null;
    const medicationCode = opt.medicationCode || opt.drugbank_id || '';
    const medicationName = opt.medicationName || opt.name || '';
    if (!medicationCode || !medicationName) return null;
    return {
      medicationCode,
      medicationName,
      ...(opt.medicationType ? { medicationType: opt.medicationType } : {}),
      ...(opt.drugbank_id ? { drugbank_id: opt.drugbank_id } : {}),
    };
  };

  const getPreSelectedOtherMedications = () => {
    const fromOptions = (options || []).filter(
      (opt: any) => opt?.medicationCode && opt?.medicationName
    );
    if (fromOptions.length) return fromOptions;

    if (!Array.isArray(defaultValue) || defaultValue.length === 0) return [];

    return defaultValue
      .map(normalizeMedicationEntry)
      .filter(Boolean);
  };

  const commitMedicationFormValues = async (valueToCommit: any) => {
    Inputs[index]?.fields?.forEach((i: any) => {
      if (
        i?.systemFieldName === 'OtherMedication' ||
        i?.systemFieldName === 'MedicationPanel'
      ) {
        i.defaultValue = valueToCommit;
      }
    });

    const newInputs = await assignFormValues(
      Inputs,
      index,
      depControlIndex,
      fieldIndex,
      valueToCommit,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      undefined,
      setInputs
    );

    if (!ArrayReqId) {
      if (setInputs) setInputs([...newInputs]);
    } else if (setInfectiousData && Array.isArray(infectiousData)) {
      const infectiousDataCopy = JSON.parse(JSON.stringify(infectiousData));
      const infectiousIndex = FindIndex(infectiousData, ArrayReqId);
      if (infectiousIndex !== -1) {
        infectiousDataCopy[infectiousIndex].sections = newInputs;
        setInfectiousData([...infectiousDataCopy]);
      }
    }

    return newInputs;
  };

  const commitAssignedMedicationFormValues = async (valueToCommit: any) => {
    Inputs[index]?.fields?.forEach((i: any) => {
      if (
        i?.systemFieldName === 'MedicationPanel' ||
        i?.systemFieldName === 'AssignedMedications'
      ) {
        i.defaultValue = valueToCommit;
      }
    });

    const newInputs = await assignFormValues(
      Inputs,
      index,
      depControlIndex,
      fieldIndex,
      valueToCommit,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      undefined,
      setInputs
    );

    if (!ArrayReqId) {
      if (setInputs) setInputs([...newInputs]);
    } else if (setInfectiousData && Array.isArray(infectiousData)) {
      const infectiousDataCopy = JSON.parse(JSON.stringify(infectiousData));
      const infectiousIndex = FindIndex(infectiousData, ArrayReqId);
      if (infectiousIndex !== -1) {
        infectiousDataCopy[infectiousIndex].sections = newInputs;
        setInfectiousData([...infectiousDataCopy]);
      }
    }

    return newInputs;
  };

  const {
    suggestions,
    handleChange,
    handleKeyDown,
    handleClick,
    searchedValue,
    setSearchedValue,
    setTouched,
    setSuggestions
  } = UseMedicationAutoComplete(inputSearchRef, ArrayReqId, fieldConfiguration);

  const [AssignedMedicationList, setAssignedMedicationList] = useState<any>(
    sysytemFieldName === 'AssignedMedications'
      ? options.filter((opt: any) => opt.isSelected)
      : []
  );
  // Initialize with defaultValue but don't reset unnecessarily
  const [selectedOtherMedications, setSelectedOtherMedications] = useState<
    any[]
  >(options || []);

  // MedicationType state - auto-select "Prescribed" when ArrayReqId === 11
  const [medicationType, setMedicationType] = useState<string>(
    ArrayReqId === 11 ? 'Prescribed' : ''
  );
  // Helper function to merge medicationSelections with selectedOtherMedications
  const getMergedMedications = (
    updatedOtherMedications?: any[],
    updatedMedicationSelections?: any[]
  ) => {
    const otherMeds = Array.isArray(updatedOtherMedications)
      ? updatedOtherMedications
      : (Array.isArray(selectedOtherMedications) ? selectedOtherMedications : []);
    // Callers removing a medication must pass the next list explicitly: the
    // `setMedicationSelections` that precedes the call has not applied yet, so
    // reading state here would merge the removed medication straight back in.
    const selectionMeds = Array.isArray(updatedMedicationSelections)
      ? updatedMedicationSelections
      : (Array.isArray(medicationSelections) ? medicationSelections : []);

    // Convert medicationSelections to match selectedOtherMedications format
    const convertedSelections = selectionMeds.map((med: any) => ({
      medicationName: med.name || med.displayFieldName || '',
      medicationCode: med.drugbank_id || '',
      medicationType: med.value === 'prescribed' ? 'Prescribed' : med.value === 'considered' ? 'Considered' : 'Prescribed',
      drugbank_id: med.drugbank_id,
      systemFieldName: med.systemFieldName,
      displayFieldName: med.displayFieldName
    }));

    // Merge arrays, avoiding duplicates based on medication name
    const merged = [...otherMeds];
    convertedSelections.forEach((convertedMed: any) => {
      const convertedMedName = (convertedMed.medicationName || '').trim().toLowerCase();
      const exists = merged.some((med: any) => {
        const medName = (med.medicationName || med.name || med.displayFieldName || '').trim().toLowerCase();
        return medName === convertedMedName && medName !== '';
      });
      if (!exists) {
        merged.push(convertedMed);
      }
    });

    return merged;
  };

  const updateFields = async (
    code: string,
    isSelected: boolean,
    selectedMedications: any
  ) => {
    Inputs[index]?.fields?.map((i: any) => {
      if (i?.systemFieldName === 'MedicationPanel') {
        i.defaultValue = selectedMedications;
      }
    });
    if (Inputs[index]?.fields) {
      const medication = Inputs[index].fields
        .find((field: any) => field.systemFieldName === 'AssignedMedications')
        ?.medicationList.find((med: any) => med.medicationCode === code);
      if (medication) medication.isSelected = isSelected;
    }

    const newInputs = await assignFormValues(
      Inputs,
      index,
      depControlIndex,
      fieldIndex,
      selectedMedications,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      undefined,
      setInputs
    );

    if (!ArrayReqId) {
      if (setInputs) setInputs([...newInputs]);
    } else if (setInfectiousData && Array.isArray(infectiousData)) {
      const infectiousDataCopy = JSON.parse(JSON.stringify(infectiousData));
      const infectiousIndex = FindIndex(infectiousData, ArrayReqId);
      if (infectiousIndex !== -1) {
        infectiousDataCopy[infectiousIndex].sections = newInputs;
        setInfectiousData([...infectiousDataCopy]);
      }
    }
  };

  const handleChangeCheckbox = (event: any, code: any, name: any) => {
    const isMedicationAlreadyAssigned = (medicationCode: any) => {
      // Check if Inputs[index] and fields exist
      const fields = Inputs[index]?.fields;
      if (!fields) return false;

      // Get the field index
      const fieldIndex = FindIndex(fields, 'OtherMedication');
      if (fieldIndex === -1) return false; // Assuming FindIndex returns -1 if not found

      // Check if defaultValue is an array
      const defaultValue = fields[fieldIndex]?.defaultValue;
      if (!Array.isArray(defaultValue)) return false;

      // Check if medicationCode exists in defaultValue
      return defaultValue.some(
        (med: any) => med.medicationCode === medicationCode
      );
    };

    if (isMedicationAlreadyAssigned(code)) {
      toast.error('This medication is already selected.');
      return;
    }
    const medicationObj = { medicationCode: code, medicationName: name };
    const updatedList = event.target.checked
      ? [...AssignedMedicationList, medicationObj]
      : AssignedMedicationList.filter(
        (med: any) => med.medicationCode !== code
      );

    Inputs[index]?.fields?.map((i: any) => {
      if (i?.systemFieldName === 'MedicationPanel') {
        i.defaultValue = updatedList;
      }
    });
    setAssignedMedicationList(updatedList);
    updateFields(code, event.target.checked, updatedList);
  };

  const isMedicationAlreadySelected = (medicationName: string) => {
    if (!medicationName || typeof medicationName !== 'string') return false;

    const normalizedName = medicationName.trim().toLowerCase();

    // Check selectedOtherMedications
    if (selectedOtherMedications.some(
      (med: any) => {
        const medName = (med.medicationName || med.name || '').trim().toLowerCase();
        return medName === normalizedName;
      }
    )) {
      return true;
    }

    // Check medicationSelections
    if (Array.isArray(medicationSelections) && medicationSelections.some(
      (med: any) => {
        const medName = (med.name || med.displayFieldName || '').trim().toLowerCase();
        return medName === normalizedName;
      }
    )) {
      return true;
    }

    // Check AssignedMedications
    const assignedMedicationsField = Inputs[index]?.fields?.find(
      (element: any) => element.systemFieldName === 'AssignedMedications'
    );
    if (assignedMedicationsField?.medicationList?.some(
      (med: any) => {
        const medName = (med.medicationName || '').trim().toLowerCase();
        return med.isSelected && medName === normalizedName;
      }
    )) {
      return true;
    }

    return false;
  };

  const handleOtherMedication = async (item: any) => {
    // If ArrayReqId is 11, require MedicationType to be selected
    if (ArrayReqId === 11 && !medicationType) {
      toast.error('Please select Medication Type before adding medication.');
      setSearchedValue('');
      setSuggestions([]);
      return;
    }

    const medicationName = item.medicationName || item.name;
    if (isMedicationAlreadySelected(medicationName)) {
      toast.error('This medication is already selected.');
      setSearchedValue('');
      setSuggestions([]);
      return;
    }

    const medicationObj = {
      medicationName: medicationName,
      medicationCode: item.medicationCode || item.drugbank_id,
      ...(ArrayReqId === 11 && medicationType ? { medicationType } : {}),
    };
    const updatedList = [...selectedOtherMedications, medicationObj];
    setSelectedOtherMedications(updatedList);
    setSearchedValue('');

    // Get merged medications including medicationSelections - pass updatedList to avoid stale state
    const mergedMedications = getMergedMedications(updatedList);
    await commitMedicationFormValues(mergedMedications);
  };

  // Clears the per-medication fields the PGX Medical Necessity section carries
  // for a medication that is being removed.
  const clearMedicalNecessityFields = (predicate: (field: any) => boolean) => {
    if (!Array.isArray(Inputs)) return;
    const medicalNecessityIndex = Inputs.findIndex(
      (item: any) => item?.sectionId === 89
    );
    if (medicalNecessityIndex === -1 || !Inputs[medicalNecessityIndex]?.fields) return;

    Inputs[medicalNecessityIndex].fields.forEach((field: any) => {
      if (!predicate(field)) return;
      field.defaultValue = '';
      if (field.options) {
        field.options.forEach((option: any) => {
          option.isSelectedDefault = false;
        });
      }
    });
  };

  /**
   * Single removal path for both medication tables.
   *
   * The table rows come from two different sources - `selectedOtherMedications`
   * (typed under Other) and `medicationSelections` (picked in the PGX section) -
   * so a row is identified by `systemFieldName` when it has one and by name
   * otherwise. Both lists are pruned and the merged result is committed once, so
   * `OtherMedication` / `MedicationPanel` stop carrying the removed medication.
   * Previously the row disappeared from the table (real React state) while
   * `Inputs` kept the value, which left it in the payload and let the
   * "select at least one option" rule pass with nothing selected.
   */
  const removeMedicationEntry = async ({
    medicationName,
    systemFieldName,
  }: {
    medicationName?: string;
    systemFieldName?: string;
  }) => {
    const normalizedName = (medicationName || '').trim().toLowerCase();
    if (!normalizedName && !systemFieldName) return;

    const matchesRemoved = (candidateName: string, candidateField?: string) => {
      if (systemFieldName && candidateField) return candidateField === systemFieldName;
      return Boolean(normalizedName) && candidateName.trim().toLowerCase() === normalizedName;
    };

    const nextOtherMedications = (
      Array.isArray(selectedOtherMedications) ? selectedOtherMedications : []
    ).filter(
      (med: any) =>
        !matchesRemoved(med.medicationName || med.name || '', med.systemFieldName)
    );

    const nextMedicationSelections = (
      Array.isArray(medicationSelections) ? medicationSelections : []
    ).filter(
      (med: any) =>
        !matchesRemoved(med.name || med.displayFieldName || '', med.systemFieldName)
    );

    setSelectedOtherMedications(nextOtherMedications);
    if (setMedicationSelections) setMedicationSelections(nextMedicationSelections);

    if (ArrayReqId === 11) {
      clearMedicalNecessityFields((field: any) =>
        systemFieldName
          ? field?.systemFieldName === systemFieldName
          : Boolean(medicationName) &&
            String(field?.systemFieldName || '').includes(medicationName as string)
      );
    }

    const mergedMedications = getMergedMedications(
      nextOtherMedications,
      nextMedicationSelections
    );

    Inputs[index]?.fields?.forEach((i: any) => {
      if (
        i?.systemFieldName === 'MedicationPanel' ||
        i?.systemFieldName === 'OtherMedication'
      ) {
        i.defaultValue = mergedMedications;
        i.medicationList = mergedMedications;
      }
    });

    await commitMedicationFormValues(mergedMedications);
  };

  const removeMedication = async (medicationName: string) => {
    if (!medicationName || typeof medicationName !== 'string') return;
    await removeMedicationEntry({ medicationName });
  };
  // Load saved medication values when editing an existing requisition.
  useEffect(() => {
    if (noActiveMedication || !location?.state?.reqId) return;

    if (
      sysytemFieldName === 'OtherMedication' &&
      !hasInitializedOtherMedicationsRef.current
    ) {
      const preSelected = getPreSelectedOtherMedications();
      hasInitializedOtherMedicationsRef.current = true;

      if (preSelected.length === 0) return;

      setSelectedOtherMedications(preSelected);
      commitMedicationFormValues(preSelected);
      return;
    }

    if (
      sysytemFieldName === 'AssignedMedications' &&
      Array.isArray(options) &&
      !hasInitializedAssignedMedicationsRef.current
    ) {
      const preSelected = options.filter((opt: any) => opt?.isSelected);
      hasInitializedAssignedMedicationsRef.current = true;

      if (preSelected.length === 0) return;

      setAssignedMedicationList(preSelected);
      commitAssignedMedicationFormValues(preSelected);
    }
  },
    [
      noActiveMedication,
      location?.state?.reqId,
      sysytemFieldName,
      defaultValue,
      options,
    ]);

  useEffect(() => {
    // Auto-select "Prescribed" when ArrayReqId === 11, but only if noActiveMedication is false
    if (ArrayReqId === 11 && !medicationType && !noActiveMedication) {
      setMedicationType('Prescribed');
    }
  }, [ArrayReqId, medicationType, noActiveMedication]);

  // Merge PGX medication radio selections into OtherMedication when they change
  useEffect(() => {
    if (sysytemFieldName !== 'OtherMedication' || noActiveMedication) return;
    if (!Array.isArray(medicationSelections) || medicationSelections.length === 0) {
      return;
    }

    commitMedicationFormValues(getMergedMedications());
  }, [medicationSelections, ArrayReqId, sysytemFieldName, noActiveMedication]);

  useEffect(() => {

    if (noActiveMedication) {
      setSelectedOtherMedications([]);
      setSearchedValue('');
      setTouched(false);
      setSuggestions([]);
      setMedicationType(""); // Clear MedicationType when noActiveMedication is true
    }
  }, [noActiveMedication]);
  return (
    <>
      <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
        {ArrayReqId === 11 && (
          <div className="mb-3">
            <label className="form-label fw-500 mb-2">Medication Type</label>
            <div className="d-flex gap-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="medicationType"
                  id="medicationTypePrescribed"
                  value="Prescribed"
                  checked={medicationType === 'Prescribed'}
                  onChange={(e) => setMedicationType(e.target.value)}
                  disabled={noActiveMedication}
                />
                <label className="form-check-label" htmlFor="medicationTypePrescribed">
                  Prescribed
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="medicationType"
                  id="medicationTypeConsidered"
                  value="Considered"
                  checked={medicationType === 'Considered'}
                  onChange={(e) => setMedicationType(e.target.value)}
                  disabled={noActiveMedication}
                />
                <label className="form-check-label" htmlFor="medicationTypeConsidered">
                  Considered
                </label>
              </div>
            </div>
          </div>
        )}
        <h6 className="mb-3">{t(displayFieldName)}</h6>
        {sysytemFieldName === 'AssignedMedications' && (
          <div className="row">
            {options
              ?.slice() // alphabatic order
              .sort((a: any, b: any) =>
                a?.medicationName?.localeCompare(b?.medicationName)
              )
              .map(({ medicationCode, medicationName, isSelected }: any) => (
                <div
                  className="col-lg-4 col-md-6 col-6 py-1"
                  key={medicationCode}
                >
                  <div className="form__group form__group--checkbox mb-3">
                    <label
                      className={
                        labelClassName
                          ? `${labelClassName} fw-400 text-break`
                          : 'form-check form-check-inline form-check-solid m-0 fw-400 text-break'
                      }
                    >
                      <input
                        className="form-check-input h-20px w-20px"
                        type="checkbox"
                        checked={isSelected}
                        onChange={e =>
                          handleChangeCheckbox(e, medicationCode, medicationName)
                        }
                        disabled={noActiveMedication}
                      />
                      {loading ? (
                        <MuiSkeleton height={22} />
                      ) : (
                        <span className={spanClassName}>{medicationName}</span>
                      )}
                    </label>
                  </div>
                  {error && (
                    <div className="form__error">
                      <span>{t(error)}</span>
                    </div>
                  )}
                </div>
              ))}

          </div>
        )}

        {sysytemFieldName === 'OtherMedication' && (
          <div className="mt-2">
            {/* MedicationType Radio Buttons - Only show when ArrayReqId === 11 */}

            {/* Search Input Container */}
            <div className="mb-3">
              <div
                style={{ position: 'relative', width: '100%', maxWidth: '400px' }}
              >
                <input
                  type="text"
                  name="Medications"
                  placeholder={t('Medications')}
                  className={
                    noActiveMedication || (ArrayReqId === 11 && !medicationType)
                      ? 'form-control bg-secondary'
                      : 'form-control'
                  }
                  value={searchedValue}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  ref={inputSearchRef}
                  disabled={noActiveMedication || (ArrayReqId === 11 && !medicationType)}
                />
                {suggestions.length > 0 && (
                  <div
                    className="dropdown-menu show w-100"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      marginTop: '2px',
                      display: 'block',
                    }}
                  >
                    {suggestions.map((item: any) => (
                      <button
                        // `drugbank_id` is shared by every synonym of a drug
                        // (DB13943 covers six names), so it is not unique on its
                        // own - pair it with the name, which is distinct.
                        key={`${item.medicationName || item.name}-${item.medicationCode || item.drugbank_id}`}
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          handleClick(item);
                          handleOtherMedication(item);
                        }}
                      >
                        {item.medicationName || item.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Display selected medications */}
            {ArrayReqId === 11 && (
              (Array.isArray(selectedOtherMedications) && selectedOtherMedications.length > 0) ||
              (Array.isArray(medicationSelections) && medicationSelections.length > 0)
            ) ? (
              <div className="mt-4 w-100">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="fw-600">Medication Name</th>
                      <th className="fw-600">Medication Code</th>
                      <th className="fw-600">Type</th>
                      <th className="fw-600 text-center" style={{ width: '100px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Display medications from selectedOtherMedications */}
                    {Array.isArray(selectedOtherMedications) && selectedOtherMedications.map(
                      ({ medicationCode, medicationName, drugbank_id, name, medicationType: medType }: any) => (
                        <tr key={`other-${medicationName || name}-${medicationCode || drugbank_id}`}>
                          <td>{medicationName || name}</td>
                          <td>{medicationCode || drugbank_id}</td>
                          <td>{medType || 'Prescribed'}</td>
                          <td className="text-center">
                            <i
                              className="bi bi-x-lg cursor-pointer text-danger"
                              onClick={() => {
                                void removeMedicationEntry({
                                  medicationName: medicationName || name,
                                });
                              }}
                              style={{ fontSize: '1.2rem' }}
                            ></i>
                          </td>
                        </tr>
                      )
                    )}
                    {/* Display medications from medicationSelections */}
                    {Array.isArray(medicationSelections) && medicationSelections.map(
                      ({ drugbank_id, name, displayFieldName, value, systemFieldName }: any, rowIndex: number) => (
                        <tr key={`selection-${systemFieldName}-${rowIndex}`}>
                          <td>{name || displayFieldName}</td>
                          <td>{drugbank_id || '-'}</td>
                          <td>{value === 'prescribed' ? 'Prescribed' : value === 'considered' ? 'Considered' : 'Prescribed'}</td>
                          <td className="text-center">
                            <i
                              className="bi bi-x-lg cursor-pointer text-danger"
                              onClick={() => {
                                void removeMedicationEntry({
                                  medicationName: name || displayFieldName,
                                  systemFieldName,
                                });
                              }}
                              style={{ fontSize: '1.2rem' }}
                            ></i>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2 mt-4 w-100">
                {Array.isArray(selectedOtherMedications) &&
                  selectedOtherMedications.map(
                    ({ medicationCode, medicationName, drugbank_id, name }: any) => (
                      <div
                        className="d-flex badge badge-secondary px-2 fw-500 gap-2 align-items-center pt-2"
                        key={`${medicationName || name}-${medicationCode || drugbank_id}`}
                      >
                        <i
                          className="bi bi-x-lg cursor-pointer"
                          onClick={() => removeMedication(medicationName || name)}
                        ></i>
                        <span>{medicationName || name}</span>
                      </div>
                    )
                  )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ActiveMedicationListCheckbox;

